import React, { useState, useEffect } from 'react';
import { FaStar, FaQuoteLeft, FaQuoteRight } from 'react-icons/fa';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase/config';
import './Testimonials.css';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    console.log('🔄 Chargement des avis depuis Realtime Database...');
    
    const avisRef = ref(database, 'avis');
    
    const unsubscribe = onValue(avisRef, (snapshot) => {
      const data = snapshot.val();
      console.log('📦 Données brutes de Firebase:', data);
      
      if (data) {
        const avisList = Object.entries(data).map(([id, avis]) => {
          let authorName = 'Client';
          
          if (avis.userName) {
            authorName = avis.userName;
          } else if (avis.userPrenom && avis.userNom) {
            authorName = `${avis.userPrenom} ${avis.userNom}`;
          } else if (avis.userPrenom) {
            authorName = avis.userPrenom;
          } else if (avis.userEmail) {
            authorName = avis.userEmail.split('@')[0];
          } else if (avis.userId) {
            authorName = `Client ${avis.userId.substring(0, 5)}`;
          }
          
          return {
            id,
            author: authorName,
            location: 'Client vérifié',
            rating: avis.note || 5,
            text: avis.commentaire || 'Aucun commentaire',
            date: avis.date ? new Date(avis.date).toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : new Date().toLocaleDateString('fr-FR'),
            valide: avis.valide || false
          };
        }).filter(avis => avis.valide === true);
        
        console.log('✅ Avis formatés et validés:', avisList);
        setTestimonials(avisList);
        setLoading(false);
      } else {
        console.log('⚠️ Aucun avis trouvé dans la base de données');
        setTestimonials([
          {
            id: 1,
            author: "Jean Dupont",
            location: "Paris",
            rating: 5,
            text: "Service impeccable, équipe très professionnelle. Je recommande vivement !",
            date: "Mars 2026",
            valide: true
          },
          {
            id: 2,
            author: "Marie Martin",
            location: "Lyon",
            rating: 5,
            text: "Déménagement rapide et soigné. Prix très compétitifs.",
            date: "Février 2026",
            valide: true
          },
          {
            id: 3,
            author: "Pierre Durand",
            location: "Bordeaux",
            rating: 5,
            text: "Une équipe à l'écoute et efficace. Merci pour ce déménagement réussi.",
            date: "Janvier 2026",
            valide: true
          }
        ]);
        setLoading(false);
      }
    }, (error) => {
      console.error('❌ Erreur lors du chargement des avis:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const nextTestimonial = () => {
    if (testimonials.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }
  };

  const prevTestimonial = () => {
    if (testimonials.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  if (loading) {
    return (
      <section className="testimonials-section" id="testimonials">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Chargement des avis...</p>
          </div>
        </div>
      </section>
    );
  }

  const totalAvis = testimonials.length;
  const moyenneGenerale = totalAvis > 0 
    ? (testimonials.reduce((acc, t) => acc + t.rating, 0) / totalAvis).toFixed(1)
    : '4.9';
  const tauxSatisfaction = totalAvis > 0 
    ? Math.round((testimonials.filter(t => t.rating >= 4).length / totalAvis) * 100)
    : 98;

  return (
    <section className="testimonials-section" id="testimonials">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">AVIS CLIENTS</span>
          <h2 className="section-title">
            Ce que nos <span className="text-gradient">clients disent</span>
          </h2>
          <p className="section-description">
            Découvrez les retours d'expérience de nos clients satisfaits
          </p>
        </div>

        <div className="testimonials-stats">
          <div className="stat-item">
            <span className="stat-number">{moyenneGenerale}</span>
            <span className="stat-label">Note moyenne</span>
            <div className="stat-stars">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="star-filled" />
              ))}
            </div>
          </div>
          <div className="stat-item">
            <span className="stat-number">{totalAvis}</span>
            <span className="stat-label">Avis vérifiés</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{tauxSatisfaction}%</span>
            <span className="stat-label">Clients satisfaits</span>
          </div>
        </div>

        {testimonials.length > 0 ? (
          <>
            <div className="testimonials-carousel">
              <button className="carousel-btn prev" onClick={prevTestimonial} aria-label="Avis précédent">
                ←
              </button>

              <div className="carousel-track">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className={`testimonial-card ${index === currentIndex ? 'active' : ''}`}
                    style={{
                      transform: `translateX(${(index - currentIndex) * 100}%)`,
                      opacity: index === currentIndex ? 1 : 0,
                      zIndex: index === currentIndex ? 2 : 1,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      transition: 'all 0.5s ease',
                      pointerEvents: index === currentIndex ? 'auto' : 'none'
                    }}
                  >
                    <div className="card-inner">
                      <FaQuoteLeft className="quote-icon quote-left" />
                      <FaQuoteRight className="quote-icon quote-right" />

                      <div className="client-info">
                        <div className="avatar">
                          {testimonial.author?.charAt(0).toUpperCase() || 'C'}
                        </div>
                        <div className="client-details">
                          <h4>{testimonial.author}</h4>
                          <p>{testimonial.location}</p>
                        </div>
                      </div>

                      <div className="rating">
                        {[...Array(5)].map((_, i) => (
                          <FaStar
                            key={i}
                            className={i < testimonial.rating ? 'star-filled' : 'star-empty'}
                          />
                        ))}
                      </div>

                      <div className="testimonial-text">
                        <p>"{testimonial.text}"</p>
                      </div>

                      <div className="testimonial-date">
                        {testimonial.date}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button className="carousel-btn next" onClick={nextTestimonial} aria-label="Avis suivant">
                →
              </button>
            </div>

            <div className="carousel-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Aller à l'avis ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="no-avis">
            <p>Aucun avis pour le moment. Soyez le premier à donner votre avis !</p>
          </div>
        )}

        <div className="testimonials-cta">
          <p>Vous avez déménagé avec nous ?</p>
          <button className="btn-primary" onClick={() => window.location.href = '/dashboard/avis'}>
            Laissez votre avis
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;